import React from 'react';
import { Users } from 'lucide-react';

export interface Membership {
  id: string;
  marcaTemporal: string;
  nombres: string;
  apellidos: string;
  fechaNacimiento: string;
  genero: string;
  estadoCivil: string;
  telefono: string;
  email: string;
  zonaBarrio: string;
  numeroDocumento?: string;
  tipoDocumento?: string;
  consentimientoDatos: boolean;
  autorizacionImagen: boolean;
  observacionesLegales?: string;
  creadoEn: string;
  actualizadoEn: string;
  estado: string;
}

interface MembershipsTableProps {
  memberships: Membership[];
  onMemberClick: (member: Membership) => void;
  isLoading: boolean;
}

export const MembershipsTable: React.FC<MembershipsTableProps> = ({
  memberships,
  onMemberClick,
  isLoading
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getMemberRowClass = (member: Membership) => {
    const hasMissingData = !member.numeroDocumento || !member.email;
    
    if (hasMissingData) {
      return "hover:bg-red-100 cursor-pointer transition-colors border-l-4 border-red-400 bg-red-50";
    }
    
    return "hover:bg-gray-50 cursor-pointer transition-colors";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Miembro
            </th>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Contacto
            </th>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Documento
            </th>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Fecha de Registro
            </th>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Estado
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.isArray(memberships) && memberships.map((member) => (
            <tr 
              key={member.id} 
              className={getMemberRowClass(member)}
              onClick={() => onMemberClick(member)}
              title={(!member.numeroDocumento || !member.email) ? 
                `Datos faltantes: ${!member.numeroDocumento ? 'Documento' : ''} ${(!member.numeroDocumento && !member.email) ? 'y ' : ''}${!member.email ? 'Email' : ''}` : 
                undefined}
            >
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="shrink-0 h-8 w-8 md:h-10 md:w-10">
                    <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-gradient-to-r from-purple-400 to-blue-400 flex items-center justify-center">
                      <span className="text-white text-xs md:text-xs font-medium">
                        {member.nombres.charAt(0)}{member.apellidos.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3 md:ml-4">
                    <div className="text-xs md:text-xs font-medium text-gray-900">
                      {member.nombres} {member.apellidos}
                    </div>
                    <div className="text-xs md:text-xs text-gray-500">
                      {member.genero} • {member.estadoCivil}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap">
                <div className="text-xs md:text-xs text-gray-900">
                  {member.email || (
                    <span className="inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-medium rounded-full bg-red-100 text-red-800">
                      ⚠ Email faltante
                    </span>
                  )}
                </div>
                <div className="text-xs md:text-xs text-gray-500">{member.telefono}</div>
                <div className="text-xs md:text-xs text-gray-500">{member.zonaBarrio}</div>
              </td>
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap">
                {member.numeroDocumento && member.tipoDocumento ? (
                  <div>
                    <div className="text-xs md:text-xs text-gray-900">{member.tipoDocumento}</div>
                    <div className="text-xs md:text-xs text-gray-500">{member.numeroDocumento}</div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-medium rounded-full bg-red-100 text-red-800">
                      ⚠ Faltante
                    </span>
                  </div>
                )}
              </td>
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap text-xs md:text-xs text-gray-500">
                {formatDate(member.creadoEn)}
              </td>
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap">
                <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-medium rounded-full ${
                  member.estado === 'activo' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {member.estado}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Empty state */}
      {Array.isArray(memberships) && memberships.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-xs font-medium text-gray-900">No hay membresías registradas</h3>
          <p className="mt-1 text-xs text-gray-500">Los miembros registrados aparecerán aquí.</p>
        </div>
      )}
    </div>
  );
};

export default MembershipsTable;